import React, { useRef, createElement } from 'react';
import { VARIANT_COLORS } from './DjayAvatar';
// Import note (audit-referentie: Ronde 10): `createElement` is NOT needed —
// JSX is transformed by the bundler. Regressed in ronde 6 and ronde 8.
export type DjayToetsDomain = 'rekenen' | 'taalverzorging' | 'begrijpend-lezen' | 'toets';
export interface DjayToetsAvatarProps {
  domain?: DjayToetsDomain;
  size?: 'sm' | 'md' | 'lg';
  animate?: boolean;
  className?: string;
}
const SIZES = {
  sm: 48,
  md: 80,
  lg: 120
};
// Reuse the canonical default-variant palette so this avatar never drifts
// out of sync with DjayAvatar.
const colors = VARIANT_COLORS.default;
let _stylesInjected = false;
function ensureAnimationStyles() {
  if (_stylesInjected || typeof document === 'undefined') return;
  _stylesInjected = true;
  const style = document.createElement('style');
  style.setAttribute('data-djay-toets-avatar', 'animations');
  style.textContent = `
    .djay-toets-animated .djay-eyes { transform-origin: center; animation: djay-blink 4s ease-in-out infinite; }
    @keyframes djay-blink { 0%, 90%, 100% { transform: scaleY(1); } 95% { transform: scaleY(0.1); } }
    .djay-toets-animated .djay-body { transform-origin: center bottom; animation: djay-wobble 6s ease-in-out infinite; }
    @keyframes djay-wobble { 0%, 100% { transform: rotate(0deg); } 25% { transform: rotate(-1.5deg); } 75% { transform: rotate(1.5deg); } }
    .djay-toets-animated .djay-flame { transform-origin: center bottom; animation: djay-flame-flicker 3s ease-in-out infinite; }
    @keyframes djay-flame-flicker { 0%, 100% { transform: scaleY(1) scaleX(1); } 33% { transform: scaleY(1.08) scaleX(0.95); } 66% { transform: scaleY(0.95) scaleX(1.05); } }
  `;
  document.head.appendChild(style);
}
export function DjayToetsAvatar({
  domain = 'toets',
  size = 'md',
  animate = false,
  className = ''
}: DjayToetsAvatarProps) {
  if (animate) {
    ensureAnimationStyles();
  }
  const uid = useRef(Math.random().toString(36).slice(2, 9)).current;
  const pixelSize = SIZES[size];
  const renderHeldItem = () => {
    switch (domain) {
      case 'rekenen':
        return <g transform="translate(155, 90) rotate(15)" data-id="element-3559" data-mp="366ce1yldot0d">
            <rect x="0" y="0" width="24" height="36" rx="3" fill="#CBD5E1" stroke="#94A3B8" strokeWidth="2" data-id="element-3560" data-mp="317rbvtmkfz9a" />
            <rect x="4" y="4" width="16" height="8" rx="1" fill="#86EFAC" data-id="element-3561" data-mp="3twpwqyi2pfia" />
            <rect x="4" y="16" width="4" height="4" rx="1" fill="#475569" data-id="element-3562" data-mp="3oz6ja0t796re" />
            <rect x="10" y="16" width="4" height="4" rx="1" fill="#475569" data-id="element-3563" data-mp="3jyis9f4z0px6" />
            <rect x="16" y="16" width="4" height="4" rx="1" fill="#475569" data-id="element-3564" data-mp="3k0v55dvvga3d" />
            <rect x="4" y="22" width="4" height="4" rx="1" fill="#475569" data-id="element-3565" data-mp="3ezpfjoxid2y2" />
            <rect x="10" y="22" width="4" height="4" rx="1" fill="#475569" data-id="element-3566" data-mp="28kodr2lsxqjk" />
            <rect x="16" y="22" width="4" height="4" rx="1" fill="#475569" data-id="element-3567" data-mp="23kiuoatllcd3" />
            <rect x="4" y="28" width="10" height="4" rx="1" fill="#475569" data-id="element-3568" data-mp="j31b8z8n9bk1" />
            <rect x="16" y="28" width="4" height="4" rx="1" fill="#F59E0B" data-id="element-3569" data-mp="j0omo38ds2y0" />
          </g>;
      case 'taalverzorging':
        return <g transform="translate(165, 85) rotate(35)" data-id="element-3570" data-mp="t0nqxnxrp752">
            <path d="M0 0 L6 0 L4 30 L2 30 Z" fill="#1E3A8A" data-id="element-3571" data-mp="sz4c81rrf45e" />
            <path d="M0 0 L3 -8 L6 0 Z" fill="#FBBF24" data-id="element-3572" data-mp="xsfle9k1nx0h" />
            <path d="M3 -8 L3 0" stroke="#B45309" strokeWidth="0.5" data-id="element-3573" data-mp="12r0l87vdweoe" />
          </g>;
      case 'begrijpend-lezen':
        return <g transform="translate(145, 100) rotate(-10)" data-id="element-3574" data-mp="16r0yhp6qyjtv">
            <path d="M0 0 Q10 -5 20 0 L20 20 Q10 15 0 20 Z" fill="#FEF3C7" stroke="#D97706" strokeWidth="1" data-id="element-3575" data-mp="1brorggejdaqq" />
            <path d="M20 0 Q30 -5 40 0 L40 20 Q30 15 20 20 Z" fill="#FEF3C7" stroke="#D97706" strokeWidth="1" data-id="element-3576" data-mp="1go5ok03pqzfy" />
            <path d="M20 0 L20 20" stroke="#D97706" strokeWidth="2" data-id="element-3577" data-mp="3ii4z2j90hcrl" />
            <line x1="4" y1="4" x2="16" y2="4" stroke="#D97706" strokeWidth="1" strokeOpacity="0.5" data-id="element-3578" data-mp="1csjcv7mdcskz" />
            <line x1="4" y1="8" x2="16" y2="8" stroke="#D97706" strokeWidth="1" strokeOpacity="0.5" data-id="element-3579" data-mp="1ltr10wv68d1c" />
            <line x1="4" y1="12" x2="12" y2="12" stroke="#D97706" strokeWidth="1" strokeOpacity="0.5" data-id="element-3580" data-mp="u3crtdy3row0" />
            <line x1="24" y1="4" x2="36" y2="4" stroke="#D97706" strokeWidth="1" strokeOpacity="0.5" data-id="element-3581" data-mp="12xj1m9ouawdy" />
            <line x1="24" y1="8" x2="36" y2="8" stroke="#D97706" strokeWidth="1" strokeOpacity="0.5" data-id="element-3582" data-mp="6diiv7gwmkh1" />
            <line x1="24" y1="12" x2="32" y2="12" stroke="#D97706" strokeWidth="1" strokeOpacity="0.5" data-id="element-3583" data-mp="1021vow3tvid" />
          </g>;
      case 'toets':
      default:
        return <g transform="translate(155, 90) rotate(10)" data-id="element-3584" data-mp="f038zm2ypz6a">
            <rect x="0" y="0" width="26" height="34" rx="2" fill="#D4A373" data-id="element-3585" data-mp="f0mhydamxd86" />
            <rect x="2" y="4" width="22" height="28" fill="#FFFFFF" data-id="element-3586" data-mp="jy4vyi3iein1" />
            <rect x="8" y="-2" width="10" height="6" rx="1" fill="#9CA3AF" data-id="element-3587" data-mp="525n358ksegu" />
            <circle cx="13" cy="1" r="1" fill="#4B5563" data-id="element-3588" data-mp="549pfiqtt5f0" />
            <path d="M6 10 L10 14 L18 6" fill="none" stroke="#22C55E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-3589" data-mp="9zp1c559wr8s" />
            <path d="M6 20 L10 24 L18 16" fill="none" stroke="#22C55E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-3590" data-mp="2up4ngmtjrrs3" />
          </g>;
    }
  };
  return <div className={`inline-flex items-center justify-center ${animate ? 'djay-toets-animated' : ''} ${className}`} style={{
    width: pixelSize,
    height: pixelSize
  }} data-id="element-3591" data-mp="rhe4k946ydb1">
      <svg viewBox="0 0 200 220" width="100%" height="100%" fill="none" xmlns="http://www.w3.org/2000/svg" style={{
      overflow: 'visible'
    }} data-id="element-3592" data-mp="hlau8fz6r8d0">
        <defs data-id="element-3593" data-mp="8k344l6tpdu0">
          <linearGradient id={`${uid}-body-grad`} x1="100" y1="45" x2="100" y2="195" gradientUnits="userSpaceOnUse" data-id="element-3594" data-mp="8lo10hgm18v1">
            <stop offset="0%" stopColor={colors.bodyGradient[0]} data-id="element-3595" data-mp="22vhxrd2yfxf1" />
            <stop offset="50%" stopColor={colors.bodyGradient[1]} data-id="element-3596" data-mp="1xzaopbf9i5sv" />
            <stop offset="100%" stopColor={colors.bodyGradient[2]} data-id="element-3597" data-mp="25j23aqsert1z" />
          </linearGradient>
        </defs>

        {/* Left arm resting */}
        <path d="M48 150 C38 142, 24 140, 18 152 C12 164, 22 178, 32 176 C42 174, 34 160, 24 158 C18 156, 20 168, 26 172" stroke={colors.armColor} strokeWidth="7" strokeLinecap="round" fill="none" data-id="element-3598" data-mp="2afk63l46hhbo" />
        <path d="M46 148 C38 141, 26 140, 20 150" stroke={colors.armHighlight} strokeWidth="3" strokeLinecap="round" fill="none" data-id="element-3599" data-mp="1mk7ojhsy2slt" />
        <circle cx="26" cy="171" r="4" fill={colors.armColor} data-id="element-3600" data-mp="1wh2lvxdhemyi" />
        <circle cx="26" cy="171" r="2.5" fill={colors.armHighlight} data-id="element-3601" data-mp="1wjot1vwgn0g7" />

        {/* Right arm extended (behind item) */}
        <path d="M152 140 Q165 125 170 115" stroke={colors.armColor} strokeWidth="8" strokeLinecap="round" fill="none" data-id="element-3602" data-mp="37wr5cerj13uk" />
        <path d="M152 140 Q165 125 170 115" stroke={colors.armHighlight} strokeWidth="3" strokeLinecap="round" fill="none" data-id="element-3603" data-mp="3cxydiexl051r" />

        <g className="djay-body" data-id="element-3604" data-mp="3t2mmgnwhnlje">
          <path d="M100 195 C55 195, 35 155, 35 115 C35 65, 60 45, 100 45 C140 45, 165 65, 165 115 C165 155, 145 195, 100 195 Z" fill={`url(#${uid}-body-grad)`} data-id="element-3605" data-mp="bkbkhwubtlge" />
        </g>

        <g className="djay-flame" data-id="element-3606" data-mp="kkq8j3yodenl">
          <path d="M100 52 C88 46, 83 34, 86 18 C90 8, 100 2, 100 2 C100 2, 110 8, 114 18 C117 34, 112 46, 100 52 Z" fill={colors.flameColors[0]} data-id="element-3607" data-mp="kjoqugn5ltww" />
          <path d="M100 50 C89 45, 85 34, 88 20 C91 10, 100 5, 100 5 C100 5, 109 10, 112 20 C115 34, 111 45, 100 50 Z" fill={colors.flameColors[1]} data-id="element-3608" data-mp="ujfm4kjjgcy1" />
          <path d="M100 48 C93 44, 90 35, 92 24 C95 15, 100 11, 100 11 C100 11, 105 15, 108 24 C110 35, 107 44, 100 48 Z" fill={colors.flameColors[2]} data-id="element-3609" data-mp="2wa0x037k4v5x" />
          <path d="M97 40 C94 36, 93 29, 95 23 C96 19, 98 17, 98 17 C98 17, 96 24, 97 31 C97 35, 97 38, 97 40 Z" fill={colors.flameColors[3]} data-id="element-3610" data-mp="16tovjrwgn9tt" />
        </g>

        <g className="djay-glasses" data-id="element-3611" data-mp="x3lt713g8jit">
          <path d="M90 92 C96 87, 104 87, 110 92" stroke={colors.glassesFrame} strokeWidth="4" strokeLinecap="round" fill="none" data-id="element-3612" data-mp="x2kgytap5qzi" />
          <rect x="49" y="82" width="42" height="36" rx="7" ry="7" fill="none" stroke={colors.glassesFrame} strokeWidth="5" data-id="element-3613" data-mp="o1vztpq0baie" />
          <rect x="52" y="85" width="36" height="30" rx="5" ry="5" fill={colors.glassesLens} data-id="element-3614" data-mp="e6u9wrnwq4er" />
          <rect x="55" y="87" width="12" height="6" rx="3" ry="3" fill="white" fillOpacity="0.55" data-id="element-3615" data-mp="28jk9nq0yi7u2" />
          <rect x="109" y="82" width="42" height="36" rx="7" ry="7" fill="none" stroke={colors.glassesFrame} strokeWidth="5" data-id="element-3616" data-mp="2jdp0e2s7bgrg" />
          <rect x="112" y="85" width="36" height="30" rx="5" ry="5" fill={colors.glassesLens} data-id="element-3617" data-mp="2efvvre6jquta" />
          <rect x="115" y="87" width="12" height="6" rx="3" ry="3" fill="white" fillOpacity="0.55" data-id="element-3618" data-mp="375w3dksfmlzu" />
          <path d="M49 96 L40 94" stroke={colors.glassesFrame} strokeWidth="4" strokeLinecap="round" data-id="element-3619" data-mp="2x6dld9w31aaf" />
          <path d="M151 96 L160 94" stroke={colors.glassesFrame} strokeWidth="4" strokeLinecap="round" data-id="element-3620" data-mp="1gv9mhw4dspfu" />
        </g>

        {/* Eyes: Focused/Attentive */}
        <g className="djay-eyes" data-id="element-3621" data-mp="3v0o8m5b43nr3">
          <path d="M62 98 Q70 92 78 98" stroke={colors.mouthColor} strokeWidth="3" strokeLinecap="round" fill="none" data-id="element-3622" data-mp="3uzmozcg15sxr" />
          <path d="M122 98 Q130 92 138 98" stroke={colors.mouthColor} strokeWidth="3" strokeLinecap="round" fill="none" data-id="element-3623" data-mp="8t7yjye83huy" />
          <circle cx="70" cy="102" r="4" fill={colors.mouthColor} data-id="element-3624" data-mp="3caq1brf51yoi" />
          <circle cx="130" cy="102" r="4" fill={colors.mouthColor} data-id="element-3625" data-mp="3c8dd4wises30" />
        </g>

        {/* Mouth: Slight smile */}
        <path d="M92 125 Q100 132 108 125" stroke={colors.mouthColor} strokeWidth="3" strokeLinecap="round" fill="none" data-id="element-3626" data-mp="3lb4pqzneftw8" />

        <g className="djay-feet" data-id="element-3627" data-mp="2xixphpejz2rj">
          <ellipse cx="80" cy="196" rx="14" ry="6" fill={colors.feetDark} data-id="element-3628" data-mp="2tki6dwlleeje" />
          <ellipse cx="80" cy="195" rx="14" ry="6" fill={colors.feetLight} data-id="element-3629" data-mp="wjcaphs2ic5h" />
          <ellipse cx="120" cy="196" rx="14" ry="6" fill={colors.feetDark} data-id="element-3630" data-mp="wgzsd3iedzrw" />
          <ellipse cx="120" cy="195" rx="14" ry="6" fill={colors.feetLight} data-id="element-3631" data-mp="11gkvlq4louaf" />
        </g>

        {/* Held Item */}
        {renderHeldItem()}

        {/* Right hand (thumb/fingers) over the item */}
        <circle cx="168" cy="112" r="4" fill={colors.armColor} data-id="element-3632" data-mp="1b7feqlmjvdrn" />
        <circle cx="168" cy="112" r="2.5" fill={colors.armHighlight} data-id="element-3633" data-mp="dl01xx52j4af" />
      </svg>
    </div>;
}